import { ScaleControl, ZoomControls } from '@ethings/ui-toolkit/map';
import { useTheme } from '@mui/material';
import L from 'leaflet';
import { FC, useRef } from 'react';
import { MapContainer, useMapEvents } from 'react-leaflet';
import { useMapZoomLimits, useSavedPosition, useSmallScreen } from '../../hooks';
import { MapStorageKeys } from '../../types';
import { ValidateResize, wrapLongitude } from '../../util';
import { FlexBox } from '../flex-box';
import { MapLayerController } from './map-layer-controller';
import { PageMapProps, ZOOM } from './page-map.types';
import { SyncZoomLimits } from './sync-zoom-limits';

const BOUNDS_OF_EARTH = L.latLngBounds(L.latLng(-85, -Infinity), L.latLng(85, Infinity));

export const SavePosition: FC<{ initialCenter: L.LatLng; storageKey: MapStorageKeys }> = ({ initialCenter, storageKey }) => {
  const { setSavedPosition } = useSavedPosition(storageKey);
  const lastSavedKeyRef = useRef<string | null>(null);

  const map = useMapEvents({
    dragend: () => updatePositionAndZoom(),
    moveend: () => updatePositionAndZoom(),
    zoomend: () => updatePositionAndZoom(),
  });

  const updatePositionAndZoom = (): void => {
    const { lat, lng } = map.getCenter() ?? initialCenter;
    const wrappedLng = wrapLongitude(lng);

    if (lng > 180 || lng < -180) {
      map.setView(new L.LatLng(lat, wrappedLng), map.getZoom(), { animate: false });
      return;
    }

    const zoom = map.getZoom();
    const saveKey = `${lat.toFixed(6)}:${wrappedLng.toFixed(6)}:${zoom.toFixed(3)}`;
    if (lastSavedKeyRef.current === saveKey) return;

    lastSavedKeyRef.current = saveKey;
    const savedPosition = new L.LatLng(lat, wrappedLng, zoom);
    setSavedPosition({ ...savedPosition, timeStamp: Date.now() });
  };
  return null;
};

export const PageMap: FC<PageMapProps> = ({
  center,
  children,
  storageKey,
  zoom = ZOOM.earth,
  bounds,
  dim,
  ...props
}) => {
  const theme = useTheme();
  const isSmallScreen = useSmallScreen();
  const { minZoom, maxZoom } = useMapZoomLimits();

  return (
    <FlexBox
      id="map-page"
      sx={{
        '& .leaflet-overlay-pane': { outline: 'none !important' },
        '& .leaflet-container': { fontFamily: 'inherit', width: '100%', height: '100%' },
        '& .leaflet-container a': { color: theme.palette.info.main, textDecoration: 'none' },
        '& .leaflet-div-icon': { background: 'none', border: 'none' },
        '& .leaflet-map-pane canvas': { filter: dim ? 'brightness(0.5)' : 'none', transition: 'filter .3s' },
        '& .leaflet-layer canvas': { backgroundColor: dim ? 'rgba(0, 0, 0, 0.5) !important' : 'none', transition: 'filter .3s' },
        '& .leaflet-marker-icon': { outline: 'none' },
        '& .leaflet-popup-content': { margin: 0 },
        '& .leaflet-tooltip-bottom': { marginTop: '20px' },
        '& .leaflet-tooltip-pane': { zIndex: 599 },
        position: 'relative',
      }}
      {...props}
    >
      <MapContainer
        center={center}
        maxBounds={BOUNDS_OF_EARTH}
        maxBoundsViscosity={1}
        maxZoom={maxZoom}
        minZoom={minZoom}
        preferCanvas={true}
        renderer={new L.Canvas()}
        touchZoom={true}
        worldCopyJump={false}
        zoom={zoom}
        zoomAnimation={true}
        zoomAnimationThreshold={2}
        zoomControl={false}
        zoomDelta={1}
        zoomSnap={0.5}
      >
        <SyncZoomLimits />
        <ScaleControl imperial />
        <ValidateResize />
        <MapLayerController drawerLayerSelection={isSmallScreen} />
        {storageKey && <SavePosition initialCenter={center} storageKey={storageKey} />}
        <ZoomControls bottom={3} right={2} />
        {children}
      </MapContainer>
    </FlexBox>
  );
};
